// src/CompoundInterestCalculator.js
import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSchema from '../../FAQSchema';
import AdBanner from '../AdPage/AdBanner';

// Register necessary components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend
);

const CompoundInterestCalculator = () => {
  const [principalAmount, setPrincipalAmount] = useState(10000);
  const [interestRate, setInterestRate] = useState(5);
  const [timePeriod, setTimePeriod] = useState(2);
  const [compoundFrequency, setCompoundFrequency] = useState('yearly');
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const calculateCompoundInterest = () => {
    let n;
    switch (compoundFrequency) {
      case 'quarterly':
        n = 4;
        break;
      case 'halfyearly':
        n = 2;
        break;
      case 'yearly':
      default:
        n = 1;
        break;
    }

    const rate = interestRate / 100;
    const amount = principalAmount * Math.pow((1 + rate / n), n * timePeriod);
    const interest = amount - principalAmount;

    setTotalInterest(interest.toFixed(2));
    setTotalAmount(amount.toFixed(2));
  };

  const handlePrincipalAmountChange = (e) => {
    const value = e.target.value;
    setPrincipalAmount(value === '' ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === '' ? 0 : parseFloat(value));
  };

  const handleTimePeriodChange = (e) => {
    const value = e.target.value;
    setTimePeriod(value === '' ? 0 : parseInt(value));
  };

  const handleCompoundFrequencyChange = (e) => {
    setCompoundFrequency(e.target.value);
  };

  const data = {
    labels: ['Principal Amount', 'Total Interest'],
    datasets: [
      {
        label: 'Compound Interest Breakdown',
        data: [principalAmount, totalInterest],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
      {
        question: 'What is compound interest?',
        answer: 'Compound interest is interest earned on both the principal and the accumulated interest from previous periods.',
      },
      {
        question: 'How is compound interest different from simple interest?',
        answer: 'Simple interest is calculated only on the principal, while compound interest also includes accumulated interest in its calculations.',
      },
      {
        question: 'Can I use this calculator for SIPs?',
        answer: 'Yes, though SIPs typically use a different formula, this calculator helps project returns if reinvested regularly.',
      },
      {
        question: 'What does compounding frequency mean?',
        answer: 'It refers to how often the interest is added to the principal—monthly, quarterly, semi-annually, or annually.',
      },
      {
        question: 'Is compound interest better for long-term investments?',
        answer: 'Yes, the effect of compounding magnifies over time, making it ideal for long-term wealth growth.',
      },
      {
        question: 'Can I calculate yearly growth using this tool?',
        answer: 'Yes, the calculator can show how your investment grows year after year.',
      },
      {
        question: 'Is this calculator suitable for fixed deposits (FDs)?',
        answer: 'Absolutely. You can use it to estimate returns on FDs with compounding interest.',
      },
      {
        question: 'How accurate is this calculator?',
        answer: 'It is designed using standard financial formulas and provides reliable estimates based on your input.',
      },
      {
        question: 'Can I use it for recurring deposits (RDs)?',
        answer: 'You can approximate RD returns if you use monthly compounding and adjust the investment term accordingly.',
      },
      {
        question: 'Can it show future value of mutual funds?',
        answer: 'Yes, it offers a ballpark figure for SIPs or lump sum investments, though SIP calculators are more precise for regular investments.',
      },
      {
        question: 'Is there a mobile version of the calculator?',
        answer: 'Yes, TheFinMaster\'s website is mobile-optimized for easy access.',
      },
      {
        question: 'Does it include inflation impact?',
        answer: 'No, the calculator gives nominal future values. To adjust for inflation, you need to subtract the average inflation rate from your interest rate.',
      },
      {
        question: 'Can I save my calculations?',
        answer: 'While the calculator doesn’t store data, you can take screenshots or download results for reference.',
      },
      {
        question: 'Does compounding work the same for loans and investments?',
        answer: 'The principle is the same, but for loans, compounding works against you as interest accumulates on borrowed amounts.',
      },
      {
        question: 'Do I need to register to use the calculator?',
        answer: 'No, the calculator is free to use without any registration or login requirements.',
      },
    ]
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
     <Helmet>
         <title>Compound Interest Calculator – Future Value of Investment</title>
        <meta  name='description' content='Estimate the future value of your investments using compound interest calculator. Plan your savings smartly.'/>
        <meta name='keywords' content='compound interest calculator, daily compound interest calculator, compound growth rate calculator, annual compound rate calculator, ci formula, composite interest formula, compound interest calculator compounded daily, compound interest calculator in indian rupees, compounded annual growth rate formula' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/compound-interest-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Compound Interest Calculator</span>
        </li> 
      
      </ol>
    </nav> */}
       <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
    <div className='py-4'>
    
    <div className="container mx-auto p-4 shadow-xl rounded-md ">
            <h1 className="text-2xl font-bold mb-4">Compound Interest Calculator</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Principal Amount</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                value={principalAmount}
                onChange={handlePrincipalAmountChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">₹ {principalAmount.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="10000000"
              value={principalAmount}
              onChange={(e) => setPrincipalAmount(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Rate of Interest (P.A.)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={interestRate}
                onChange={handleInterestRateChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{interestRate} %</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(parseFloat(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Time Period (Years)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="50"
                value={timePeriod}
                onChange={handleTimePeriodChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{timePeriod} Yr</span>
            </div>
            <input
              type="range"
              min="0"
              max="50"
              value={timePeriod}
              onChange={(e) => setTimePeriod(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Compound Frequency</label>
            <select
              value={compoundFrequency}
              onChange={handleCompoundFrequencyChange}
              className="w-full p-2 border rounded"
            >
              <option value="yearly">Yearly</option>
              <option value="halfyearly">Half-Yearly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>
          <button
            onClick={calculateCompoundInterest}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Calculate
          </button>
        </div>
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-bold">Results</h2>
            <p>Principal Amount: ₹ {principalAmount.toLocaleString()}</p>
            <p>Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}</p>
            <p>Total Amount (Principal + Interest): ₹ {parseFloat(totalAmount).toLocaleString()}</p>
          </div>
          <div>
            <h2 className="text-xl font-bold">Breakdown</h2>
            <div className="flex justify-center">
              <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
    <div className="w-full md:w-2/3 p-4 mx-auto">
    <div className="container mx-auto py-4">
          <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            TheFinMaster’s Compound Interest Calculator: Unlock the Power of Wealth Creation
          </h2>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Compound interest is a powerful tool for growing your wealth over time. By earning interest not only on your initial investment but also on the accumulated interest, it creates exponential growth. TheFinMaster’s Compound Interest Calculator makes it easy to visualize this growth, helping you plan smarter for your financial future.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is Compound Interest?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Compound interest is the interest calculated not only on the initial principal but also on the accumulated interest from previous periods. This contrasts with simple interest, where interest is calculated only on the principal. With compounding, your investment earns "interest on interest," leading to exponential growth.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Let’s say you invest Rs. 10,000 at a 10% annual interest rate. After the first year, you earn Rs. 1,000, bringing your total to Rs. 11,000. In the second year, the 10% is calculated on Rs. 11,000—resulting in Rs. 1,100 in interest. This cumulative growth continues for as long as your money remains invested.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using TheFinMaster's Compound Interest Calculator
            </h2>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>See Your Money Grow:</strong> Instead of manually crunching numbers or using spreadsheets, our calculator gives you a clear picture of how your investments will perform over time, including year-by-year breakdowns.</li>
              <li><strong>Set Realistic Goals:</strong> By knowing how much your money can grow, you can plan the necessary investment amount to meet specific targets—be it retirement, children’s education, or a dream home.</li>
              <li><strong>Compare Investment Options:</strong> Want to know whether a 5-year FD or a 7-year PPF gives better returns? The calculator lets you compare different interest rates, tenures, and principal amounts side by side.</li>
              <li><strong>Flexible Variables:</strong> You can adjust the frequency of compounding—monthly, quarterly, semi-annually, or annually—depending on your chosen investment scheme.</li>
              <li><strong>Stay on Track:</strong> Regularly revisiting your compound interest projections helps you monitor performance and make timely adjustments if needed.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How TheFinMaster’s Compound Interest Calculator Works
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Our calculator simplifies a traditionally complex concept. It uses the formula:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>A = P (1 + r/n) ^ (n × t)</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>A is the maturity amount</li>
              <li>P is the principal investment</li>
              <li>r is the annual interest rate (in decimal)</li>
              <li>n is the number of times interest is compounded per year</li>
              <li>t is the time in years</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              All you need to do is enter:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Your initial investment amount (principal)</li>
              <li>The annual interest rate</li>
              <li>The investment period</li>
              <li>The compounding frequency</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              With a single click, you get the total maturity value and the interest earned.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Example: Visualizing the Power of Compounding
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Let’s consider an investment of Rs. 50,000 at a 10% annual interest rate compounded yearly over 5 years.
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using the compound interest formula:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Maturity Value = Rs. 79,861</li>
              <li>Interest Earned = Rs. 29,861</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Had this been a simple interest calculation, the earnings would have been only Rs. 25,000. The difference lies in compounding.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Key Use Cases for Compound Interest
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Compound interest is widely applied in various financial products, including:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Recurring Deposits & Fixed Deposits</li>
              <li>Public Provident Fund (PPF)</li>
              <li>Mutual Fund SIPs</li>
              <li>Retirement Savings Plans</li>
              <li>Educational Investment Funds</li>
              <li>National Savings Certificate (NSC)</li>
              <li>Endowment Life Insurance Policies</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Whether you're a salaried employee, entrepreneur, or student, understanding compound interest is essential to achieving your financial milestones.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Compound Interest Beats Simple Interest in the Long Run
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              While simple interest is easier to understand, compound interest offers better returns over time. The longer you stay invested, the more significant the impact of compounding. It encourages early and consistent investing—two key pillars of long-term wealth building.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Additionally, compound interest supports:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Reinvestment Discipline:</strong> Automatically adds interest to the base.</li>
              <li><strong>Long-Term Vision:</strong> Motivates you to stay invested for bigger gains.</li>
              <li><strong>Wealth Accumulation:</strong> Suitable for goal-based investing.</li>
            </ul>
          </div>
          <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Tips to Maximize Compound Interest
            </h2>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Start Early:</strong> Even small investments made early can outgrow larger investments made later.</li>
              <li><strong>Invest for Longer Durations:</strong> The compounding effect strengthens over time.</li>
              <li><strong>Choose Higher Compounding Frequencies:</strong> Quarterly or monthly compounding gives slightly better returns than annual.</li>
              <li><strong>Reinvest Your Earnings:</strong> Avoid premature withdrawals for maximum gain.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Start Using The Calculator Today
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Take charge of your finances by trying out TheFinMaster's Compound Interest Calculator. It’s user-friendly, 100% free, and optimized for quick results. Whether you're planning an SIP, retirement corpus, or just saving up for a future goal, this tool can be your best companion.
            </p>
          </div>

       
        </div>

     <div className="w-full md:w-1/3 p-4">
       <div className="bg-white shadow-md rounded-lg overflow-hidden">
         <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
           <h2 className="text-lg font-semibold ">Popular Categories</h2>
         </div>
         <div className="p-2 ">
         <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>
                          {/* Ad Banner */}
                      <div className="mt-4">
                        <div className="bg-white shadow-md rounded-lg overflow-hidden">
                          <div className="p-3 w-full h-auto rounded-md object-cover">
                            <AdBanner displayOrder="2" />
                          </div>
                        </div>
                      </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                {/* <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link> */}
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
             {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
           </ul>
         </div>
       </div>

     </div>

   </div>

   <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h2>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
    </>
  );
};

export default CompoundInterestCalculator;
